import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { QUICK_RANGES } from "../../../../utils/nutrition-progress";
import { getDateToDisplay } from "../../../../utils/nutrition-helpers";
import Dropdown from "../../../../parts/dropdown";
import DateInput from "../../../../parts/inputs/date-input";

// Persistent range controls (PRD's three headers): nutrition-plan selector,
// From/To date inputs, quick-select buttons. Purely presentational — every
// value and handler comes from useNutritionProgressFilters via the parent.
const RangeControls = ({
  nutritionPlans,
  selectedPlanId,
  onSelectPlan,
  dateFrom,
  dateTo,
  onChangeDate,
  activeQuick,
  onApplyQuickRange,
}) => {
  const { t } = useTranslation("athlete-portal");

  const selectedPlan = nutritionPlans.find((p) => String(p._id) === selectedPlanId);
  const planSummaryText = selectedPlan
    ? `${getDateToDisplay(selectedPlan.utcStartDate)} - ${getDateToDisplay(selectedPlan.utcEndDate)}`
    : t("nutritionPage.progress.selectPlanPlaceholder");

  return (
    <div className="np-filters">
      {/* Nutrition plan selector */}
      <div className="np-filters__field">
        <span className="np-filters__label">{t("nutritionPage.progress.nutritionPlan")}</span>
        <Dropdown
          summaryText={planSummaryText}
          options={nutritionPlans.map((plan) => ({
            text: `${getDateToDisplay(plan.utcStartDate)} - ${getDateToDisplay(plan.utcEndDate)}`,
            value: plan._id,
          }))}
          onSelectFn={(value) => onSelectPlan(String(value))}
        />
      </div>

      {/* Reporting period: from → to */}
      <div className="np-filters__field">
        <span className="np-filters__label">{t("nutritionPage.progress.reportingPeriod")}</span>
        <div className="np-filters__period">
          <DateInput
            name="np-from"
            value={dateFrom}
            max={dateTo}
            onChange={(date) => onChangeDate(date, "dateFrom")}
          />
          <span className="np-filters__sep">{t("nutritionPage.progress.to")}</span>
          <DateInput
            name="np-to"
            value={dateTo}
            min={dateFrom}
            onChange={(date) => onChangeDate(date, "dateTo")}
            alignRight
          />
        </div>
      </div>

      {/* Quick select */}
      <div className="np-filters__quick">
        {QUICK_RANGES.map((q) => (
          <button
            key={q.id}
            data-button-type={activeQuick === q.id ? "outline" : "secondary"}
            onClick={() => onApplyQuickRange(q.id)}
          >
            <span>{t(`nutritionPage.progress.${q.id}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

RangeControls.propTypes = {
  nutritionPlans: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedPlanId: PropTypes.string,
  onSelectPlan: PropTypes.func.isRequired,
  dateFrom: PropTypes.instanceOf(Date),
  dateTo: PropTypes.instanceOf(Date),
  onChangeDate: PropTypes.func.isRequired,
  activeQuick: PropTypes.string,
  onApplyQuickRange: PropTypes.func.isRequired,
};

export default RangeControls;
